package org.example.project.engineservice.support

import org.example.project.dashboard.DashboardTheme

import androidx.compose.runtime.Composable
import org.example.project.service.ZustandManager
import org.example.project.settings.UISettings
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

private const val SYS_SUPPORT_DETAIL_MAX_WIDTH = 980

@Composable
internal fun SysSupportLayout(
    internalTitleVisible: Boolean,
    tickets: List<All>,
    filteredTickets: List<All>,
    ticketFilter: String,
    offeneTickets: Int,
    abgeschlosseneTickets: Int,
    selectedTicket: All?,
    manuelleLoesung: String,
    saving: Boolean,
    onFilterChange: (String) -> Unit,
    onBack: () -> Unit,
    onOpenTicket: (All) -> Unit,
    onManualSolutionChange: (String) -> Unit,
    onCloseTicket: () -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            width(100.percent)
            height(100.percent)
            backgroundColor(Color.transparent)
            color(Color.white)
            property("font-family", "Inter, system-ui, sans-serif")
            property("box-sizing", "border-box")
        }
    }) {
        Div({
            style {
                width(100.percent)
                height(100.percent)
                backgroundColor(Color.transparent)
                property("border", "none")
                borderRadius(0.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Row)
                alignItems(AlignItems.Stretch)
                property("box-sizing", "border-box")
                overflow("visible")
                padding(18.px)
            }
        }) {
            SysSupportBackButton(selectedTicket != null, onBack)

            Div({
                style {
                    flexGrow(1)
                    padding(40.px)
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    alignItems(AlignItems.Center)
                    property("box-sizing", "border-box")
                }
            }) {
                SysSupportHeader(
                    internalTitleVisible = internalTitleVisible,
                    selectedTicket = selectedTicket,
                    ticketFilter = ticketFilter,
                    ticketCount = tickets.size,
                    offeneTickets = offeneTickets,
                    abgeschlosseneTickets = abgeschlosseneTickets,
                    onFilterChange = onFilterChange
                )

                if (selectedTicket == null) {
                    SysSupportTicketList(
                        filteredTickets = filteredTickets,
                        onOpenTicket = onOpenTicket
                    )
                } else {
                    SysSupportDetailPanel(
                        selectedTicket = selectedTicket,
                        manuelleLoesung = manuelleLoesung,
                        saving = saving,
                        onManualSolutionChange = onManualSolutionChange,
                        onCloseTicket = onCloseTicket
                    )
                }
            }

            Div({ style { width(72.px); opacity(0.0); property("pointer-events", "none") } }) {}
        }
    }
}

@Composable
private fun SysSupportBackButton(isVisible: Boolean, onBack: () -> Unit) {
    Div({
        style {
            width(72.px)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            opacity(if (isVisible) 0.6 else 0.0)
            property("pointer-events", if (isVisible) "auto" else "none")
            cursor("pointer")
        }
        onClick { onBack() }
    }) {
        Img(src = "images/back.svg") {
            style {
                width(28.px)
                height(28.px)
                property("filter", "brightness(0) invert(1)")
            }
        }
    }
}

@Composable
private fun SysSupportHeader(
    internalTitleVisible: Boolean,
    selectedTicket: All?,
    ticketFilter: String,
    ticketCount: Int,
    offeneTickets: Int,
    abgeschlosseneTickets: Int,
    onFilterChange: (String) -> Unit
) {
    Div({
        style {
            width(100.percent)
            maxWidth(600.px)
            textAlign("center")
        }
    }) {
        if (internalTitleVisible) {
            H2({ style { margin(0.px) } }) {
                Text("Support Aufträge")
            }
        }
        if (selectedTicket == null || selectedTicket.zustand == ZustandManager.Support.ABGESCHLOSSEN) {
            Div({
                style {
                    marginTop(14.px)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    gap(18.px)
                }
            }) {
                Img(src = "images/filter_list_off.svg") {
                    style {
                        width(20.px)
                        height(20.px)
                        property("filter", "brightness(0) invert(1)")
                    }
                }
                Span({ style { color(Color("#7e8795")); property("color", "#7e8795") } }) { Text("|") }
                SysSupportCounterItem(
                    icon = "images/tickets.svg",
                    value = ticketCount,
                    filterKey = "ALLE",
                    ticketFilter = ticketFilter,
                    tint = "brightness(0) invert(1)",
                    numberColor = "#ffffff",
                    onFilterChange = onFilterChange
                )
                Span({ style { color(Color("#7e8795")); property("color", "#7e8795") } }) { Text("|") }
                SysSupportCounterItem(
                    icon = "images/tickets_open.svg",
                    value = offeneTickets,
                    filterKey = "OFFEN",
                    ticketFilter = ticketFilter,
                    tint = "brightness(0) saturate(100%) invert(24%) sepia(92%) saturate(2920%) hue-rotate(344deg) brightness(98%) contrast(93%)",
                    numberColor = "#ff4d4f",
                    onFilterChange = onFilterChange
                )
                Span({ style { color(Color("#7e8795")); property("color", "#7e8795") } }) { Text("|") }
                SysSupportCounterItem(
                    icon = "images/tickets_close.svg",
                    value = abgeschlosseneTickets,
                    filterKey = "CLOSE",
                    ticketFilter = ticketFilter,
                    tint = "brightness(0) saturate(100%) invert(65%) sepia(31%) saturate(690%) hue-rotate(78deg) brightness(94%) contrast(89%)",
                    numberColor = "#66BB6A",
                    onFilterChange = onFilterChange
                )
            }
        }
    }
}

@Composable
private fun SysSupportCounterItem(
    icon: String,
    value: Int,
    filterKey: String,
    ticketFilter: String,
    tint: String,
    numberColor: String,
    onFilterChange: (String) -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(8.px)
            cursor("pointer")
            opacity(if (ticketFilter == filterKey) 1.0 else 0.72)
        }
        onClick { onFilterChange(filterKey) }
    }) {
        Img(src = icon) {
            style {
                width(32.px)
                height(32.px)
                property("filter", tint)
            }
        }
        Span({
            style {
                fontSize(16.px)
                fontWeight("700")
                color(Color(numberColor))
                property("color", numberColor)
            }
        }) { Text(value.toString()) }
    }
}

@Composable
private fun SysSupportTicketList(
    filteredTickets: List<All>,
    onOpenTicket: (All) -> Unit
) {
    Div({
        style {
            flexGrow(1)
            width(100.percent)
            marginTop(34.px)
            overflowY("auto")
            property("scrollbar-width", "none")
            property("-ms-overflow-style", "none")
        }
    }) {
        if (filteredTickets.isEmpty()) {
            Div({
                style {
                    flexGrow(1)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    textAlign("center")
                    width(100.percent)
                    minHeight(320.px)
                }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        alignItems(AlignItems.Center)
                        gap(8.px)
                    }
                }) {
                    Span({ style { fontSize(18.px); fontWeight("600") } }) {
                        Text("Keine Tickets vorhanden.")
                    }
                }
            }
        } else {
            filteredTickets.forEachIndexed { index, ticket ->
                SysSupportTicketRow(
                    index = index,
                    ticket = ticket,
                    onOpenTicket = onOpenTicket
                )
            }
        }
    }
}

@Composable
private fun SysSupportTicketRow(
    index: Int,
    ticket: All,
    onOpenTicket: (All) -> Unit
) {
    val statusIcon = if (ticket.zustand == ZustandManager.Support.ABGESCHLOSSEN) {
        "images/tickets_close.svg"
    } else {
        "images/tickets_open.svg"
    }
    val statusTint = if (ticket.zustand == ZustandManager.Support.ABGESCHLOSSEN) {
        "brightness(0) saturate(100%) invert(65%) sepia(31%) saturate(690%) hue-rotate(78deg) brightness(94%) contrast(89%) drop-shadow(0 0 8px rgba(102,187,106,0.65))"
    } else {
        "brightness(0) saturate(100%) invert(24%) sepia(92%) saturate(2920%) hue-rotate(344deg) brightness(98%) contrast(93%) drop-shadow(0 0 8px rgba(255,77,79,0.65))"
    }

    Div({
        style {
            marginBottom(10.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Row)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            gap(14.px)
            width(100.percent)
            maxWidth(420.px)
            property("margin-left", "auto")
            property("margin-right", "auto")
            property("box-sizing", "border-box")
        }
    }) {
        Span({
            style {
                fontSize(14.px)
                fontWeight("700")
                width(64.px)
                minWidth(64.px)
                textAlign("right")
                alignSelf(AlignSelf.Center)
                property("box-sizing", "border-box")
            }
        }) {
            Text("#${index + 1}")
        }
        Div({
            style {
                padding(12.px, 14.px)
                borderRadius(8.px)
                backgroundColor(DashboardTheme.SURFACE_CARD)
                property("border", "1px solid rgba(255,255,255,0.16)")
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Row)
                alignItems(AlignItems.Center)
                gap(12.px)
                width(100.percent)
                maxWidth(310.px)
                cursor("pointer")
                property("box-sizing", "border-box")
            }
            onClick { onOpenTicket(ticket) }
        }) {
            Div({
                style {
                    width(52.px)
                    height(52.px)
                    position(Position.Relative)
                    flexShrink(0)
                }
            }) {
                Img(src = statusIcon) {
                    style {
                        width(44.px)
                        height(44.px)
                        position(Position.Absolute)
                        left(50.percent)
                        top(50.percent)
                        property("transform", "translate(-50%, -50%)")
                        property("filter", statusTint)
                    }
                }
            }
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(4.px)
                    flexGrow(1)
                    alignItems(AlignItems.Center)
                    textAlign("center")
                }
            }) {
                Span({ style { fontWeight("600") } }) {
                    Text(ticket.ticketnummer.ifBlank { "SUP-${ticket.id}" })
                }
                Span({ style { color(Color("#9aa4b5")); fontSize(12.px) } }) {
                    Text("Screen: ${ticket.screenKey.ifBlank { "-" }}")
                }
                Span({ style { color(Color("#9aa4b5")); fontSize(12.px) } }) {
                    Text("Zustand: ${ZustandManager.Support.textFor(ticket.zustand)}")
                }
            }
        }
    }
}

@Composable
private fun SysSupportDetailPanel(
    selectedTicket: All,
    manuelleLoesung: String,
    saving: Boolean,
    onManualSolutionChange: (String) -> Unit,
    onCloseTicket: () -> Unit
) {
    Div({
        style {
            flexGrow(1)
            width(100.percent)
            marginTop(30.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            gap(14.px)
            textAlign("center")
            backgroundColor(Color.transparent)
        }
    }) {
        Div({
            style {
                width(100.percent)
                display(DisplayStyle.Flex)
                flexWrap(FlexWrap.Wrap)
                alignItems(AlignItems.Stretch)
                gap(8.px)
                maxWidth(SYS_SUPPORT_DETAIL_MAX_WIDTH.px)
                alignSelf(AlignSelf.Center)
            }
        }) {
            SysSupportField(
                "Support Ticket",
                selectedTicket.ticketnummer.ifBlank { "SUP-${selectedTicket.id}" },
                compact = true
            )
            SysSupportField("Firma", selectedTicket.firma.ifBlank { "-" }, compact = true)
            SysSupportField("FirmenId", selectedTicket.firmenId.ifBlank { "-" }, compact = true)
            SysSupportField("Zustand", ZustandManager.Support.textFor(selectedTicket.zustand), compact = true)
            SysSupportField(
                "Mitarbeiter",
                listOf(selectedTicket.vorname, selectedTicket.nachname)
                    .filter { it.isNotBlank() }
                    .joinToString(" ")
                    .ifBlank { "-" },
                compact = true
            )
            SysSupportField("Screen", selectedTicket.screenKey.ifBlank { "-" }, compact = true)
        }

        Div({
            style {
                width(100.percent)
                maxWidth(SYS_SUPPORT_DETAIL_MAX_WIDTH.px)
                alignSelf(AlignSelf.Center)
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(2, minmax(0, 1fr))")
                gap(12.px)
            }
        }) {
            SysSupportField("Abschlussanalyse", selectedTicket.loesungsweg.ifBlank { "-" }, true)
            SysSupportField("Problembeschreibung", selectedTicket.problembeschreibung.ifBlank { "-" }, true)

            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(8.px)
                    property("grid-column", "1 / -1")
                }
            }) {
                TextArea(attrs = {
                    style {
                        width(100.percent)
                        minHeight(180.px)
                        padding(16.px)
                        borderRadius(10.px)
                        backgroundColor(Color.transparent)
                        color(Color.white)
                        property("border", "1px solid rgba(255,255,255,0.16)")
                        property("box-sizing", "border-box")
                        property("resize", "none")
                        property("caret-color", "white")
                        opacity(if (selectedTicket.zustand == ZustandManager.Support.ABGESCHLOSSEN) 0.78 else 1.0)
                    }
                    value(manuelleLoesung)
                    if (selectedTicket.zustand != ZustandManager.Support.ABGESCHLOSSEN) {
                        onInput { onManualSolutionChange(it.value) }
                    }
                    if (selectedTicket.zustand == ZustandManager.Support.ABGESCHLOSSEN) disabled()
                    placeholder("L\u00f6sung")
                })
            }
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                gap(14.px)
                width(100.percent)
                maxWidth(SYS_SUPPORT_DETAIL_MAX_WIDTH.px)
            }
        }) {
            if (selectedTicket.zustand != ZustandManager.Support.ABGESCHLOSSEN) {
                Button(attrs = {
                    style {
                        height(44.px)
                        padding(0.px, 18.px)
                        borderRadius(8.px)
                        backgroundColor(Color.transparent)
                        color(Color.white)
                        property("border", "1px solid rgba(255,255,255,0.34)")
                        property("white-space", "nowrap")
                        cursor("pointer")
                    }
                    if (saving || manuelleLoesung.isBlank()) disabled()
                    onClick {
                        if (manuelleLoesung.isBlank()) return@onClick
                        onCloseTicket()
                    }
                }) {
                    Text(if (saving) "..." else "Ticket abschlie\u00dfen")
                }
            }
        }
    }
}

@Composable
private fun SysSupportLine(label: String, value: String, preWrap: Boolean = false, compact: Boolean = false) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(if (compact) 4.px else 6.px)
            if (compact) {
                property("flex", "1 1 140px")
                minWidth(120.px)
            } else {
                width(100.percent)
            }
            padding(if (compact) 8.px else 14.px)
            borderRadius(10.px)
            property("border", "1px solid rgba(255,255,255,0.12)")
            property("box-sizing", "border-box")
            minWidth(0.px)
        }
    }) {
        Span({
            style {
                color(Color("#9aa4b5"))
                fontSize(if (compact) 10.px else 12.px)
                property("text-transform", "uppercase")
                letterSpacing(if (compact) 0.4.px else 0.6.px)
            }
        }) { Text(label) }
        Span({
            style {
                fontWeight("600")
                fontSize(if (compact) 13.px else 15.px)
                property("overflow", "hidden")
                property("text-overflow", "ellipsis")
                property("white-space", if (preWrap) "pre-wrap" else "nowrap")
                if (preWrap) property("white-space", "pre-wrap")
            }
        }) { Text(value) }
    }
}

@Composable
private fun SysSupportField(label: String, value: String, preWrap: Boolean = false, compact: Boolean = false) {
    SysSupportLine(label, value, preWrap, compact)
}
